import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { motion } from 'framer-motion';
import PlaceIcon from '@mui/icons-material/Place';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PublicIcon from '@mui/icons-material/Public';
import { Colors } from './style/theme';

const apiServices = [
  {
    title: 'IP Quality Check and Location Data API ',
    description: 'IP Quality Check and Location Data API identifies IP reputation, detects fraud, and provides precise geographic location insights.',
    icon: <PublicIcon fontSize="large" sx={{ color: Colors.primary }} />, 
    status: 'Live',
  },
  {
    title: 'IP to Currency and Location Data API',
    description: 'Retrieve the currency associated with a given IP address by querying this API. Simply input the IP address, and the API will return the corresponding currency information.',
    icon: <MonetizationOnIcon fontSize="large" sx={{ color: Colors.primary }} />, 
    status: 'Live',
  },
  {
    title: 'IP to Time-Zone and Location Data API',
    description: 'Retrieve the time zone and language associated with a given IP address using this API. Simply input the IP address, and the API will return the corresponding time zone and language details.',
    icon: <AccessTimeIcon fontSize="large" sx={{ color: Colors.primary }} />, 
    status: 'Live',
  },
  {
    title: 'Mobile Number Verification API',
    description: 'Verify phone numbers in real time to enhance security, reduce fake sign-ups, and improve user authentication.',
    icon: <PhoneAndroidIcon fontSize="large" sx={{ color: Colors.primary }} />, 
    status: 'Live',
  },
  {
    title: 'Geocoding & Reverse Geocoding API',
    description: 'Convert addresses into geographic coordinates and vice versa for accurate location mapping and enhanced user experiences.',
    icon: <PlaceIcon fontSize="large" sx={{ color: Colors.primary }} />, 
    status: 'Upcoming',
  },
  {
    title: 'Name Matching API',
    description: 'Ensure accurate identity verification with intelligent name-matching algorithms, reducing errors in customer records and fraud detection.',
    icon: <PersonSearchIcon fontSize="large" sx={{ color: Colors.primary }} />, 
    status: 'Upcoming',
  },
];

export default function APICards() {
  return (
    <Container maxWidth="lg" component="section" sx={{ py: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h4"
          sx={{ fontFamily: 'Poppins', fontWeight: '600', color: '#161414' }}
        >
          Our APIs
        </Typography>
      </Box>
      <Grid container spacing={4} justifyContent="center">
        {apiServices.map((api, index) => (
          <Grid item key={index} xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.05, rotate: 2 }} transition={{ duration: 0.3 }}>
              <Paper
                elevation={6}
                sx={{
                  width: '250px',
                  height: '360px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  borderRadius: '10px',
                  px: 2,
                  py: 3,
                  textAlign: 'center',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                  },
                  position: 'relative',
                }}
              >
                {/* Status Badge */}
                <Chip
                  label={api.status}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    backgroundColor: api.status === 'Live' ? 'green' : 'orange',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
                
                <Box sx={{ height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {api.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1, mb: 1, color: Colors.primary }}>
                  {api.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {api.description}
                </Typography>
                <Button href='/documentation' variant="contained" color="primary" size="small" sx={{ mt: 'auto' }}>
                  Learn More
                </Button>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
